import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, X, FileDown, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Bewertungs-Ansicht: MC automatisch ausgewertet, offene Fragen bepunktet der Admin.
// System schlägt Bestanden vor, wenn Prozent > pass_percent (Default 50).
export const ExamGradingModal = ({ attemptId, getAuthHeader, onClose, onSaved }) => {
  const [att, setAtt] = useState(null);
  const [openPoints, setOpenPoints] = useState({});
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiReasons, setAiReasons] = useState({});

  const runAiReview = async (force = false) => {
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/admin/exam-attempts/${attemptId}/ai-review${force ? '?force=true' : ''}`, {}, getAuthHeader());
      const sugg = r.data.ai_review?.suggestions || {};
      setOpenPoints((p) => {
        const next = { ...p };
        Object.entries(sugg).forEach(([qid, s]) => { next[qid] = s.points; });
        return next;
      });
      setAiReasons(Object.fromEntries(Object.entries(sugg).map(([qid, s]) => [qid, s.reason])));
      toast.success(r.data.cached ? 'KI-Vorbewertung geladen (gespeichert)' : 'KI-Vorbewertung fertig — bitte prüfen und ggf. anpassen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'KI-Vorbewertung fehlgeschlagen');
    }
    setAiBusy(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/exam-attempts/${attemptId}`, getAuthHeader());
        const a = r.data.attempt;
        setAtt(a);
        setOpenPoints(a.scoring?.open_points || {});
        setFeedback(a.result?.feedback || '');
        if (a.ai_review?.suggestions) {
          setAiReasons(Object.fromEntries(Object.entries(a.ai_review.suggestions).map(([qid, s]) => [qid, s.reason])));
        }
      } catch { toast.error('Versuch konnte nicht geladen werden'); onClose(); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  if (!att) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cream-50" />
      </div>
    );
  }

  const questions = att.questions || [];
  const answers = att.answers || {};
  const scoring = att.scoring || { mc_points: 0, mc_max: 0, open_max: 0, total_max: 0 };
  const openAwarded = questions.filter((q) => q.type !== 'mc' && q.type !== 'mc_multi')
    .reduce((s, q) => s + Math.min(Number(openPoints[q.id] || 0), q.points || 1), 0);
  const total = (scoring.mc_points || 0) + openAwarded;
  const totalMax = scoring.total_max || 1;
  const percent = Math.round((total / totalMax) * 1000) / 10;
  const passPct = att.pass_percent || 50;
  const suggestion = percent > passPct;

  const save = async (outcome) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/exam-attempts/${attemptId}/review`,
        { outcome, feedback, open_points: openPoints }, getAuthHeader());
      toast.success(`Bewertung gespeichert — ${percent} %`);
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setBusy(false);
  };

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/admin/exam-attempts/${attemptId}/pdf`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Pruefung_${att.user_name}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF fehlgeschlagen'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-2 sm:px-4 py-4" role="dialog" aria-modal="true">
      <div className="bg-cream-50 rounded-2xl max-w-3xl w-full max-h-[92vh] flex flex-col" data-testid="grading-modal">
        {/* Kopf mit Live-Auswertung */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-cream-300">
          <div>
            <h3 className="font-display text-xl text-espresso">Prüfung bewerten — {att.user_name}</h3>
            <p className="text-[13px] text-sand-600 mt-0.5">
              MC automatisch: <strong>{scoring.mc_points}/{scoring.mc_max} P.</strong> · Offene Fragen: <strong>{openAwarded}/{scoring.open_max} P.</strong>
            </p>
            <p className="text-[14px] mt-1" data-testid="grading-total">
              Gesamt: <strong>{total}/{totalMax} Punkte ({percent} %)</strong> —{' '}
              <span className="font-bold" style={{ color: suggestion ? '#2F7A3D' : '#A83232' }} data-testid="grading-suggestion">
                Vorschlag: {suggestion ? 'Bestanden' : 'Nicht bestanden'} (Grenze: über {passPct} %)
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => runAiReview(!!att.ai_review)}
              disabled={aiBusy}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[10.5px] font-bold uppercase tracking-wider disabled:opacity-60"
              style={{ backgroundColor: '#8F650F', color: '#FBF7F0' }}
              title="Claude bewertet die offenen Antworten vor — du prüfst und bestätigst"
              data-testid="grading-ai-btn"
            >
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {aiBusy ? 'KI bewertet…' : (att.ai_review ? 'KI erneut' : 'KI-Vorbewertung')}
            </button>
            <button onClick={downloadPdf} className="p-2 rounded-lg border border-cream-400 text-sand-700 hover:bg-cream-200" title="PDF"><FileDown className="w-4 h-4" /></button>
            <button onClick={onClose} className="p-2 rounded-lg border border-cream-400 text-sand-700 hover:bg-cream-200" data-testid="grading-close"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Fragen */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {questions.map((q, i) => {
            const isMc = q.type === 'mc';
            const isMulti = q.type === 'mc_multi';
            const sel = answers[q.id];
            const correct = isMc && Number(sel) === Number(q.correct_index);
            const selArr = isMulti ? (Array.isArray(sel) ? sel.map(Number) : []) : [];
            const correctArr = isMulti ? (q.correct_indices || []).map(Number) : [];
            const hits = selArr.filter((x) => correctArr.includes(x)).length;
            const wrongSel = selArr.filter((x) => !correctArr.includes(x)).length;
            const multiPts = correctArr.length ? Math.round(((q.points || 1) * Math.max(0, hits - wrongSel)) / correctArr.length * 2) / 2 : 0;
            return (
              <div key={q.id} className="bg-cream-100 border border-cream-300 rounded-xl p-4" data-testid={`grading-q-${i + 1}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-[13.5px] font-semibold text-espresso leading-snug">
                    {i + 1}. {q.text}
                  </p>
                  <span className="text-[11px] font-bold text-sand-500 whitespace-nowrap">{q.points} P.</span>
                </div>
                {(isMc || isMulti) ? (
                  <div className="mt-2 space-y-1">
                    {isMulti && <p className="text-[11px] font-semibold text-terra">Mehrfachauswahl — Teilpunkte automatisch</p>}
                    {(q.options || []).map((opt, oi) => {
                      const isCorrectOpt = isMulti ? correctArr.includes(oi) : oi === Number(q.correct_index);
                      const isSelOpt = isMulti ? selArr.includes(oi) : Number(sel) === oi;
                      return (
                      <p key={oi} className={`text-[12.5px] px-2.5 py-1 rounded-lg ${isCorrectOpt ? 'bg-green-50 text-green-900' : isSelOpt ? 'bg-red-50 text-red-900' : 'text-sand-600'}`}>
                        {String.fromCharCode(65 + oi)}) {opt}
                        {isCorrectOpt && ' ✓ richtig'}
                        {isSelOpt && ' — gewählt'}
                      </p>
                      );
                    })}
                    <p className="text-[12px] font-semibold mt-1" style={{ color: isMulti ? (multiPts >= (q.points || 1) ? '#2F7A3D' : multiPts > 0 ? '#8F650F' : '#A83232') : correct ? '#2F7A3D' : '#A83232' }}>
                      {isMulti
                        ? (selArr.length === 0 ? `Keine Antwort — 0/${q.points} P. (automatisch)` : `${multiPts}/${q.points} P. (automatisch)`)
                        : `${sel === undefined || sel === null ? 'Keine Antwort — 0' : correct ? `Richtig — ${q.points}` : 'Falsch — 0'}/${q.points} P. (automatisch)`}
                    </p>
                  </div>
                ) : (
                  <div className="mt-2">
                    <p className="text-[13px] text-sand-800 whitespace-pre-wrap bg-white border border-cream-300 rounded-lg px-3 py-2 max-h-48 overflow-y-auto">
                      {String(sel || '').trim() || '(keine Antwort)'}
                    </p>
                    {q.sample_solution && (
                      <p className="text-[12px] text-green-900 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1.5 mt-1.5 whitespace-pre-wrap" data-testid={`grading-solution-${i + 1}`}>
                        <strong>Musterlösung:</strong> {q.sample_solution}
                      </p>
                    )}
                    <label className="flex items-center gap-2 mt-2 text-[12.5px] text-sand-700">
                      Punkte:
                      <input
                        type="number" min={0} max={q.points} step={0.5}
                        value={openPoints[q.id] ?? ''}
                        onChange={(e) => setOpenPoints((p) => ({ ...p, [q.id]: e.target.value === '' ? '' : Math.min(Math.max(0, Number(e.target.value)), q.points) }))}
                        className="w-20 px-2 py-1.5 bg-white border border-cream-300 rounded-lg text-[13px] focus:outline-none focus:border-terra"
                        data-testid={`grading-points-${i + 1}`}
                      />
                      <span className="text-sand-500">/ {q.points}</span>
                    </label>
                    {aiReasons[q.id] && (
                      <p className="flex items-start gap-1.5 mt-1.5 text-[12px] text-sand-600 bg-[#8F650F]/5 border border-[#8F650F]/20 rounded-lg px-2.5 py-1.5" data-testid={`grading-ai-reason-${i + 1}`}>
                        <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#8F650F' }} />
                        <span><strong>KI:</strong> {aiReasons[q.id]}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Fuß: Feedback + Entscheidung */}
        <div className="p-5 border-t border-cream-300">
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={2}
            placeholder="Feedback für den Teilnehmer (optional)…"
            className="w-full px-3 py-2.5 bg-white border border-cream-300 rounded-xl text-[13px] resize-y mb-3"
            data-testid="grading-feedback"
          />
          <div className="flex flex-wrap gap-2 justify-end">
            <button onClick={() => save('nicht_bestanden')} disabled={busy}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider disabled:opacity-50 ${!suggestion ? 'ring-2 ring-offset-1 ring-red-700' : ''}`}
              style={{ backgroundColor: '#A83232', color: '#FBF7F0' }} data-testid="grading-fail">
              <XCircle className="w-3.5 h-3.5" /> Nicht bestanden
            </button>
            <button onClick={() => save('bestanden')} disabled={busy}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider disabled:opacity-50 ${suggestion ? 'ring-2 ring-offset-1 ring-green-700' : ''}`}
              style={{ backgroundColor: '#2F7A3D', color: '#FBF7F0' }} data-testid="grading-pass">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Bestanden
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamGradingModal;
